//* Libraries imports
import React from "react";
import { FaFileAlt, FaEdit } from "react-icons/fa";

//* Components imports
import Icon from "../../components/common/Icon";
import { getIcon } from "../../component/common/IconMapper";

//* Types imports
import type { FullProcess } from "../../schemas/common/process/process";
import type { ProcessCardItem } from "./featureConfig";
import {
  SUB_FEATURE_KEYS,
  SUB_FEATURE_ROUTES,
  SUB_FEATURE_DESCRIPTIONS,
  SUB_FEATURE_PARENT_MAP,
  type SubFeatureKey,
} from "../../constants/permissions";

// ============================================================================
// 📚 GUIA DE SUB FEATURES
// ============================================================================
//
// Para adicionar uma nova subFeature:
//
// 1. 🎯 SUB_FEATURE_ROUTES: Adicione a rota da subFeature
//    'parent-subfeature': '/parent/subfeature'
//
// 2. 🎨 SUB_FEATURE_ICON_MAP: Adicione o ícone (importe se necessário)
//    'parent-subfeature': FaMeuIcone
//
// 3. 🎨 SUB_FEATURE_COLOR_MAP: Adicione as cores
//    'parent-subfeature': { backgroundColor: "#hex", textColor: "#hex" }
//
// 4. 🎨 SUB_FEATURE_CLASS_MAP: Adicione as classes CSS
//    'parent-subfeature': "minhas-classes-css"
//
// 5. 📝 SUB_FEATURE_DESCRIPTION_MAP: Adicione a descrição
//    'parent-subfeature': "Descrição da minha subFeature"
//
// 6. 🔍 SUB_FEATURE_PARENT_MAP: Mapeie para o parent
//    'parent-subfeature': 'parent'
//
// ⚠️ IMPORTANTE: Apenas subFeatures que possuem TODOS os mapeamentos acima
// serão exibidas como cards. Se algum mapeamento estiver faltando,
// o card não será criado (isSubFeatureMapped retornará false).
//

const SUB_FEATURE_ICON_MAP: Record<SubFeatureKey, any> = {
  [SUB_FEATURE_KEYS.RESOURCE_QUESTION]: FaEdit, // Question editing/review
  [SUB_FEATURE_KEYS.RESOURCE_ESSAY]: FaFileAlt, // Essay document handling
} as const;

const SUB_FEATURE_COLOR_MAP: Record<
  SubFeatureKey,
  { backgroundColor: string; textColor: string }
> = {
  [SUB_FEATURE_KEYS.RESOURCE_QUESTION]: {
    backgroundColor: "#fff8e1",
    textColor: "#ff6f00",
  }, // Amber theme
  [SUB_FEATURE_KEYS.RESOURCE_ESSAY]: {
    backgroundColor: "#f3e5f5",
    textColor: "#7b1fa2",
  }, // Purple theme
} as const;

const SUB_FEATURE_CLASS_MAP: Record<SubFeatureKey, string> = {
  [SUB_FEATURE_KEYS.RESOURCE_QUESTION]: "bg-warning-soft", // Amber/warning theme
  [SUB_FEATURE_KEYS.RESOURCE_ESSAY]: "bg-purple-soft", // Purple theme
} as const;

export const getSubFeatureRoutePath = (
  subFeatureKey: string,
  processId: number
): string => {
  const routeSuffix = SUB_FEATURE_ROUTES[subFeatureKey as SubFeatureKey] || "";
  return `/process/${processId}${routeSuffix}`;
};

export const getSubFeatureIcon = (
  subFeatureKey: string,
  iconName?: string
): React.ReactNode => {
  if (iconName) {
    const mappedIcon = getIcon(iconName);
    if (mappedIcon) {
      return <Icon icon={mappedIcon} size={36} />;
    }
  }

  const iconComponent =
    SUB_FEATURE_ICON_MAP[subFeatureKey as SubFeatureKey] || FaFileAlt;
  return <Icon icon={iconComponent} size={36} />;
};

export const getSubFeatureColors = (subFeatureKey: string, color?: string) => {
  if (color && color.startsWith("#")) {
    return {
      backgroundColor: `${color}20`,
      textColor: color,
    };
  }

  return (
    SUB_FEATURE_COLOR_MAP[subFeatureKey as SubFeatureKey] || {
      backgroundColor: "#f5f5f5",
      textColor: "#666666",
    }
  );
};

export const getSubFeatureClassName = (subFeatureKey: string): string => {
  return (
    SUB_FEATURE_CLASS_MAP[subFeatureKey as SubFeatureKey] || "bg-secondary-soft"
  );
};

export const getSubFeatureDescription = (
  subFeatureKey: string,
  label: string
): string => {
  const mappedDescription =
    SUB_FEATURE_DESCRIPTIONS[subFeatureKey as SubFeatureKey];

  if (mappedDescription) {
    return mappedDescription;
  }

  return `Acesse ${label.toLowerCase()}.`;
};

export const isSubFeatureMapped = (subFeatureKey: string): boolean => {
  const key = subFeatureKey as SubFeatureKey;
  return !!(
    SUB_FEATURE_ROUTES[key] &&
    SUB_FEATURE_ICON_MAP[key] &&
    SUB_FEATURE_COLOR_MAP[key] &&
    SUB_FEATURE_CLASS_MAP[key] &&
    SUB_FEATURE_DESCRIPTIONS[key]
  );
};

export const getParentFeature = (subFeatureKey: string): string | undefined => {
  return SUB_FEATURE_PARENT_MAP[subFeatureKey];
};

export const getSubFeaturesForParent = (parentFeatureKey: string): string[] => {
  return Object.entries(SUB_FEATURE_PARENT_MAP)
    .filter(([, parent]) => parent === parentFeatureKey)
    .map(([subFeature]) => subFeature);
};

export const shouldShowOnlySubFeatures = (
  urlPath: string,
  parentFeatureKey: string
): boolean => {
  const subFeatures = getSubFeaturesForParent(parentFeatureKey);

  return subFeatures.some((subFeatureKey) => {
    const subFeaturePath = SUB_FEATURE_ROUTES[subFeatureKey as SubFeatureKey];
    return urlPath.includes(subFeaturePath);
  });
};

export const createContextualCards = (
  permissions: FullProcess["faculdadePermissions"] | undefined,
  processId: number,
  currentPath: string,
  parentFeatureKey: string
): ProcessCardItem[] => {
  const subFeatureKeys = getSubFeaturesForParent(parentFeatureKey);

  const specificSubFeature = subFeatureKeys.find((subFeatureKey) => {
    const subFeaturePath = SUB_FEATURE_ROUTES[subFeatureKey as SubFeatureKey];
    return currentPath.includes(subFeaturePath);
  });

  if (specificSubFeature) {
    return createCardsFromSubFeatures(permissions, processId, [
      specificSubFeature,
    ]);
  }

  const isInParentContext =
    currentPath.includes(`/process/${processId}/${parentFeatureKey}`) &&
    !subFeatureKeys.some((subKey) => {
      const subPath = SUB_FEATURE_ROUTES[subKey as SubFeatureKey];
      return currentPath.includes(subPath);
    });

  if (isInParentContext) {
    return createCardsFromSubFeatures(permissions, processId, subFeatureKeys);
  }

  const { createCardsFromPermissions } = require("./featureConfig");

  return createCardsFromPermissions(permissions, processId, {
    includeFeatures: true,
    includeSubFeatures: true,
    featureKeys: [parentFeatureKey],
  });
};

export const createSubFeaturesCards = (
  permissions: FullProcess["faculdadePermissions"] | undefined,
  processId: number,
  currentPath?: string
): ProcessCardItem[] => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const urlPath =
    currentPath ||
    (typeof window !== "undefined" ? window.location.pathname : "");

  const resourceSubFeatures = getSubFeaturesForParent("resource");
  const resourceCards = createCardsFromSubFeatures(
    permissions,
    processId,
    resourceSubFeatures
  );

  return resourceCards;
};

export const createCardFromSubFeature = (
  subFeature: NonNullable<
    FullProcess["faculdadePermissions"]
  >[0]["features"][0]["subFeatures"][0],
  processId: number
): ProcessCardItem | null => {
  if (!isSubFeatureMapped(subFeature.subFeatureKey)) {
    return null;
  }

  const colors = getSubFeatureColors(
    subFeature.subFeatureKey,
    subFeature.color
  );

  return {
    id: subFeature.id,
    title: subFeature.label,
    description: getSubFeatureDescription(
      subFeature.subFeatureKey,
      subFeature.label
    ),
    routePath: getSubFeatureRoutePath(subFeature.subFeatureKey, processId),
    icon: getSubFeatureIcon(subFeature.subFeatureKey, subFeature.icon),
    backgroundColor: colors.backgroundColor,
    textColor: colors.textColor,
    className: getSubFeatureClassName(subFeature.subFeatureKey),
  };
};

export const createCardsFromSubFeatures = (
  permissions: FullProcess["faculdadePermissions"] | undefined,
  processId: number,
  subFeatureKeys?: string[]
): ProcessCardItem[] => {
  if (!permissions || permissions.length === 0) {
    return [];
  }

  const cards: ProcessCardItem[] = [];

  permissions.forEach((permission) => {
    if (permission.enabled && permission.features) {
      permission.features.forEach((feature) => {
        if (feature.permissionEnabled && feature.subFeatures) {
          feature.subFeatures.forEach((subFeature) => {
            if (
              subFeature.permissionEnabled &&
              (!subFeatureKeys ||
                subFeatureKeys.includes(subFeature.subFeatureKey))
            ) {
              const card = createCardFromSubFeature(subFeature, processId);
              if (card) {
                cards.push(card);
              }
            }
          });
        }
      });
    }
  });

  return cards;
};
